import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, User, Upload, FileText, X, Camera } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import DateInput from '../components/DateInput';
import Navigation from '../components/Navigation';
import { getAdminHeaders } from '../lib/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CadastroCrianca = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    nome: '',
    data_nascimento: '',
    idade: '',
    sexo: 'M',
    pai: '',
    celular_pai: '',
    mae: '',
    celular_mae: '',
    celular_crianca: '',
    observacoes: '',
    documento_url: '',
    foto_url: '',
    is_visitor: false
  });
  const [uploading, setUploading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [docFilename, setDocFilename] = useState('');
  const [childNumero, setChildNumero] = useState(null);
  const [parentMatch, setParentMatch] = useState(null);
  const [checkingParent, setCheckingParent] = useState(false);

  useEffect(() => {
    if (editId) {
      loadChildData(editId);
    }
  }, [editId]);

  const loadChildData = async (id) => {
    try {
      const res = await axios.get(`${API}/children/${id}`);
      const child = res.data;
      setFormData({
        nome: child.nome,
        data_nascimento: child.data_nascimento,
        idade: child.idade,
        sexo: child.sexo || 'M',
        pai: child.pai || '',
        celular_pai: child.celular_pai || '',
        mae: child.mae || '',
        celular_mae: child.celular_mae || '',
        celular_crianca: child.celular_crianca || '',
        observacoes: child.observacoes || '',
        documento_url: child.documento_url || '',
        foto_url: child.foto_url || '',
        is_visitor: !!child.is_visitor
      });
      if (child.numero) setChildNumero(child.numero);
      if (child.documento_url) {
        setDocFilename('Documento anexado');
      }
    } catch (error) {
      console.error('Erro ao carregar criança:', error);
      toast.error('Erro ao carregar dados da criança');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'data_nascimento' && value) {
      const birth = new Date(value + 'T00:00:00');
      const today = new Date();
      let age = today.getFullYear() - birth.getFullYear();
      if (today.getMonth() < birth.getMonth() || (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate())) {
        age--;
      }
      setFormData(prev => ({ ...prev, [name]: value, idade: Math.max(0, age) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    // Check for existing parent when phone or name changes
    if (['celular_pai', 'celular_mae', 'pai', 'mae'].includes(name) && !editId) {
      const updatedForm = { ...formData, [name]: value };
      clearTimeout(window._parentCheckTimer);
      window._parentCheckTimer = setTimeout(() => checkExistingParent(updatedForm), 500);
    }
  };

  const checkExistingParent = async (data) => {
    const celular = data.celular_pai || data.celular_mae || '';
    const nome = data.pai || data.mae || '';
    if (celular.length < 8 && nome.length < 3) {
      setParentMatch(null);
      return;
    }
    setCheckingParent(true);
    try {
      const params = new URLSearchParams();
      if (celular.length >= 8) params.append('celular', celular);
      if (nome.length >= 3) params.append('nome', nome);
      const res = await axios.get(`${API}/children/check-parent?${params}`);
      if (res.data.found && res.data.children.length > 0) {
        setParentMatch(res.data);
      } else {
        setParentMatch(null);
      }
    } catch { setParentMatch(null); }
    finally { setCheckingParent(false); }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Foto muito grande. Max 5MB'); return; }
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/upload/document`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setFormData(prev => ({ ...prev, foto_url: res.data.url }));
      toast.success('Foto enviada!');
    } catch { toast.error('Erro ao enviar foto'); }
    finally { setUploadingPhoto(false); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const allowed = ['.pdf', '.jpg', '.jpeg', '.png', '.webp'];
    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!allowed.includes(ext)) {
      toast.error('Tipo de arquivo nao permitido. Aceitos: PDF, JPG, PNG, WEBP');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Arquivo muito grande. Maximo 5MB');
      return;
    }

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/upload/document`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData(prev => ({ ...prev, documento_url: res.data.url }));
      setDocFilename(file.name);
      toast.success('Documento anexado!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar documento');
    } finally {
      setUploading(false);
    }
  };

  const removeDocument = () => {
    setFormData(prev => ({ ...prev, documento_url: '' }));
    setDocFilename('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nome || !formData.data_nascimento) {
      toast.error('Preencha os campos obrigatórios');
      return;
    }

    if (formData.idade < 7) {
      toast.error('Não é permitido cadastrar crianças com menos de 7 anos');
      return;
    }
    if (formData.idade > 12) {
      toast.error('Não é permitido cadastrar crianças com mais de 12 anos');
      return;
    }

    // Validar que tenha pelo menos um responsável com celular
    const temPai = formData.pai && formData.celular_pai;
    const temMae = formData.mae && formData.celular_mae;
    if (!temPai && !temMae) {
      toast.error('Preencha pelo menos um responsavel (nome + celular) - pai ou mae');
      return;
    }

    setLoading(true);
    try {
      let childId;
      
      if (editId) {
        await axios.put(`${API}/children/${editId}`, {
          ...formData,
          idade: parseInt(formData.idade)
        }, { headers: getAdminHeaders() });
        toast.success('Criança atualizada com sucesso!');
        childId = editId;
      } else {
        const childRes = await axios.post(`${API}/children`, {
          ...formData,
          idade: parseInt(formData.idade)
        }, { headers: getAdminHeaders() });
        toast.success('Criança cadastrada com sucesso!');
        childId = childRes.data.id;
        
        // Criar conta de pai se tiver celular
        if (formData.celular_pai) {
          try {
            const parentRes = await axios.post(`${API}/parent/register`, {
              nome: formData.pai || 'Pai',
              celular: formData.celular_pai,
              password: '1234', // Senha padrão
              child_id: childId
            });
            
            toast.success(`Conta criada para ${formData.pai}! Senha: 1234`, {
              duration: 5000
            });
          } catch (error) {
            console.log('Pai já tem conta ou erro ao criar:', error);
          }
        }
      }
      
      navigate('/participantes');
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast.error(editId ? 'Erro ao atualizar criança' : 'Erro ao cadastrar criança');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-24">
      <div className="bg-[#1A4D2E] text-white px-6 py-6">
        <div className="max-w-3xl mx-auto">
          <button
            data-testid="back-button"
            onClick={() => navigate(-1)}
            className="mb-4 hover:bg-white/10 rounded-full p-2 transition-all"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-3">
            <User className="w-7 h-7" strokeWidth={2.5} />
            <h1 className="font-nunito font-bold text-3xl">
              {editId ? 'Editar Criança' : 'Cadastrar Criança'}
            </h1>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        <Card className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-[#E1ECE5]">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Foto 3x4 e Numero */}
            <div className="flex flex-col items-center mb-2">
              {editId && childNumero && (
                <span className="mb-2 bg-[#1A4D2E] text-white text-xs font-bold px-3 py-1 rounded-full">#{childNumero}</span>
              )}
              <div className="relative">
                {formData.foto_url ? (
                  <div className="relative">
                    <img src={`${BACKEND_URL}${formData.foto_url}`} alt="Foto" className="w-28 h-28 rounded-full object-cover border-4 border-[#1A4D2E]/20" />
                    <button type="button" onClick={() => setFormData(p => ({ ...p, foto_url: '' }))} className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1" data-testid="remove-photo">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <label className="w-28 h-28 rounded-full bg-gray-100 border-2 border-dashed border-[#1A4D2E]/30 hover:border-[#1A4D2E] flex flex-col items-center justify-center cursor-pointer transition-all" data-testid="upload-photo">
                    <Camera className="w-6 h-6 text-[#1A4D2E]/50 mb-1" />
                    <span className="text-xs text-[#1A4D2E]/50">{uploadingPhoto ? '...' : 'Foto 3x4'}</span>
                    <input type="file" accept=".jpg,.jpeg,.png,.webp" onChange={handlePhotoUpload} className="hidden" disabled={uploadingPhoto} />
                  </label>
                )}
              </div>
            </div>

            {/* Nome */}
            <div>
              <Label htmlFor="nome" className="font-inter font-semibold text-[#1A4D2E] mb-2 block">
                Nome da Criança *
              </Label>
              <Input
                id="nome"
                name="nome"
                data-testid="input-nome"
                value={formData.nome}
                onChange={handleChange}
                className="rounded-xl border-gray-200 focus:ring-2 focus:ring-[#1A4D2E] focus:border-[#1A4D2E] bg-gray-50/50"
                required
              />
            </div>

            {/* Data de Nascimento, Idade e Sexo */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="data_nascimento" className="font-inter font-semibold text-[#1A4D2E] mb-2 block">
                  Data de Nascimento *
                </Label>
                <DateInput
                  id="data_nascimento"
                  name="data_nascimento"
                  data-testid="input-data-nascimento"
                  value={formData.data_nascimento}
                  onChange={handleChange}
                  className="rounded-xl border-gray-200 focus:ring-2 focus:ring-[#1A4D2E] focus:border-[#1A4D2E] bg-gray-50/50"
                  required
                />
              </div>
              <div>
                <Label htmlFor="idade" className="font-inter font-semibold text-[#1A4D2E] mb-2 block">
                  Idade (calculada)
                </Label>
                <Input
                  id="idade"
                  name="idade"
                  type="number"
                  min="0"
                  max="18"
                  data-testid="input-idade"
                  value={formData.idade}
                  readOnly
                  className="rounded-xl border-gray-200 bg-gray-100 cursor-not-allowed"
                />
              </div>
              <div>
                <Label htmlFor="sexo" className="font-inter font-semibold text-[#1A4D2E] mb-2 block">
                  Sexo *
                </Label>
                <select
                  id="sexo"
                  name="sexo"
                  data-testid="input-sexo"
                  value={formData.sexo}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#1A4D2E] focus:border-[#1A4D2E] bg-gray-50/50 h-10 px-3 text-sm"
                >
                  <option value="M">Masculino</option>
                  <option value="F">Feminino</option>
                </select>
              </div>
            </div>

            {/* Pai */}
            <div>
              <Label htmlFor="pai" className="font-inter font-semibold text-[#1A4D2E] mb-2 block">
                Nome do Pai
              </Label>
              <Input
                id="pai"
                name="pai"
                data-testid="input-pai"
                value={formData.pai}
                onChange={handleChange}
                className="rounded-xl border-gray-200 focus:ring-2 focus:ring-[#1A4D2E] focus:border-[#1A4D2E] bg-gray-50/50"
              />
            </div>

            {/* Celular Pai */}
            <div>
              <Label htmlFor="celular_pai" className="font-inter font-semibold text-[#1A4D2E] mb-2 block">
                Celular do Pai
              </Label>
              <Input
                id="celular_pai"
                name="celular_pai"
                type="tel"
                data-testid="input-celular-pai"
                value={formData.celular_pai}
                onChange={handleChange}
                className="rounded-xl border-gray-200 focus:ring-2 focus:ring-[#1A4D2E] focus:border-[#1A4D2E] bg-gray-50/50"
                placeholder="(00) 00000-0000"
              />
            </div>

            {/* Mãe */}
            <div>
              <Label htmlFor="mae" className="font-inter font-semibold text-[#1A4D2E] mb-2 block">
                Nome da Mãe
              </Label>
              <Input
                id="mae"
                name="mae"
                data-testid="input-mae"
                value={formData.mae}
                onChange={handleChange}
                className="rounded-xl border-gray-200 focus:ring-2 focus:ring-[#1A4D2E] focus:border-[#1A4D2E] bg-gray-50/50"
              />
            </div>

            {/* Celular Mãe */}
            <div>
              <Label htmlFor="celular_mae" className="font-inter font-semibold text-[#1A4D2E] mb-2 block">
                Celular da Mãe
              </Label>
              <Input
                id="celular_mae"
                name="celular_mae"
                type="tel"
                data-testid="input-celular-mae"
                value={formData.celular_mae}
                onChange={handleChange}
                className="rounded-xl border-gray-200 focus:ring-2 focus:ring-[#1A4D2E] focus:border-[#1A4D2E] bg-gray-50/50"
                placeholder="(00) 00000-0000"
              />
            </div>

            {/* Aviso de pai/mae ja cadastrado */}
            {!editId && parentMatch && parentMatch.children.length > 0 && (
              <div data-testid="parent-match-alert" className="col-span-1 md:col-span-2 bg-amber-50 border border-amber-200 rounded-xl p-4">
                <p className="font-inter text-sm font-semibold text-amber-800 mb-1">Responsavel ja cadastrado!</p>
                <p className="font-inter text-xs text-amber-700">
                  Este responsavel ja possui {parentMatch.children.length === 1 ? '1 filho(a)' : `${parentMatch.children.length} filhos(as)`} cadastrado(s):
                </p>
                <ul className="mt-1 space-y-0.5">
                  {parentMatch.children.map((c) => (
                    <li key={c.id} className="font-inter text-xs text-amber-700 font-semibold">- {c.nome}</li>
                  ))}
                </ul>
                <p className="font-inter text-xs text-amber-600 mt-2">A nova crianca sera vinculada automaticamente a este responsavel.</p>
              </div>
            )}

            {/* Celular Criança */}
            <div>
              <Label htmlFor="celular_crianca" className="font-inter font-semibold text-[#1A4D2E] mb-2 block">
                Celular da Crianca (opcional)
              </Label>
              <Input
                id="celular_crianca"
                name="celular_crianca"
                type="tel"
                data-testid="input-celular-crianca"
                value={formData.celular_crianca}
                onChange={handleChange}
                className="rounded-xl border-gray-200 focus:ring-2 focus:ring-[#1A4D2E] focus:border-[#1A4D2E] bg-gray-50/50"
                placeholder="(00) 00000-0000"
              />
            </div>

            {/* Observações */}
            <div>
              <Label htmlFor="observacoes" className="font-inter font-semibold text-[#1A4D2E] mb-2 block">
                Observacoes
              </Label>
              <Textarea
                id="observacoes"
                name="observacoes"
                data-testid="input-observacoes"
                value={formData.observacoes}
                onChange={handleChange}
                className="rounded-xl border-gray-200 focus:ring-2 focus:ring-[#1A4D2E] focus:border-[#1A4D2E] bg-gray-50/50 min-h-[100px]"
                placeholder="Informações adicionais sobre a criança..."
              />
            </div>

            {/* Visitante */}
            <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl">
              <input type="checkbox" id="is_visitor" name="is_visitor"
                data-testid="input-is-visitor"
                checked={!!formData.is_visitor}
                onChange={(e) => setFormData(prev => ({ ...prev, is_visitor: e.target.checked }))}
                className="w-5 h-5 accent-amber-500" />
              <div className="flex-1">
                <Label htmlFor="is_visitor" className="font-inter font-semibold text-[#1A4D2E] cursor-pointer">
                  Marcar como Visitante
                </Label>
                <p className="text-xs text-gray-600">Visitantes aparecem separadamente no relatório WhatsApp enviado aos líderes de Discipulado.</p>
              </div>
            </div>

            {/* Documento de Identificação */}
            <div>
              <Label className="font-inter font-semibold text-[#1A4D2E] mb-2 block">
                Documento de Identificacao (opcional)
              </Label>
              {formData.documento_url ? (
                <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-xl">
                  <FileText className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span className="font-inter text-sm text-green-700 flex-1 truncate">{docFilename}</span>
                  <a
                    href={`${BACKEND_URL}${formData.documento_url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#1A4D2E] hover:underline text-sm font-semibold"
                    data-testid="view-document"
                  >
                    Ver
                  </a>
                  <button
                    type="button"
                    onClick={removeDocument}
                    className="text-red-500 hover:bg-red-50 rounded-full p-1"
                    data-testid="remove-document"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label
                  data-testid="upload-document"
                  className={`flex items-center justify-center gap-2 p-4 border-2 border-dashed rounded-xl cursor-pointer transition-all ${uploading ? 'border-gray-300 bg-gray-50' : 'border-[#1A4D2E]/30 hover:border-[#1A4D2E] hover:bg-[#1A4D2E]/5'}`}
                >
                  <Upload className="w-5 h-5 text-[#1A4D2E]" />
                  <span className="font-inter text-sm text-[#1A4D2E]">
                    {uploading ? 'Enviando...' : 'Clique para anexar (PDF, JPG, PNG - max 5MB)'}
                  </span>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.webp"
                    onChange={handleFileUpload}
                    className="hidden"
                    disabled={uploading}
                  />
                </label>
              )}
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              data-testid="submit-button"
              disabled={loading}
              className="w-full bg-[#1A4D2E] hover:bg-[#145A32] text-white rounded-full py-6 text-lg font-semibold shadow-md active:scale-95 transition-all"
            >
              {loading ? (editId ? 'Atualizando...' : 'Cadastrando...') : (editId ? 'Atualizar Cadastro' : 'Salvar Cadastro')}
            </Button>
          </form>
        </Card>
      </div>

      <Navigation active="cadastro" />
    </div>
  );
};

export default CadastroCrianca;
